import { useMemo, useState } from "react";
import { Calendar, MapPin, Users, ArrowUpRight, Check } from "lucide-react";
import { EVENTS } from "../data/content";

const TYPES = ["All", "Open Mic", "Book Reading", "Startup Meetup", "Workshop", "Couple Celebration", "Birthday"];

export default function Events() {
  const [type, setType] = useState("All");
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", kind: "", date: "", guests: "", note: "" });

  const filtered = useMemo(() => EVENTS.filter((e) => type === "All" || e.type === type), [type]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: "", email: "", phone: "", kind: "", date: "", guests: "", note: "" });
  };

  return (
    <div data-testid="events-page" className="pt-28">
      {/* HERO */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Events at Stories</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1]">
            Where good people <em className="italic text-[#C76D4B]">gather.</em>
          </h1>
          <p className="mt-8 text-[#7A5C45] max-w-2xl mx-auto">
            Open mics on Sundays. Founder nights on Fridays. Book clubs on Saturdays. A café
            full of community, every day of the week.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="px-5 sm:px-8">
        <div className="max-w-7xl mx-auto flex gap-2 overflow-x-auto pb-2">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`event-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setType(t)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition border ${
                type === t
                  ? "bg-[#2A1E17] text-[#FAF7F2] border-[#2A1E17]"
                  : "bg-white text-[#4A3320] border-[#E8D8C8] hover:border-[#C76D4B]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {filtered.map((ev, i) => (
            <article
              key={ev.title}
              data-testid={`event-card-${i}`}
              className="card-lift bg-white rounded-3xl border border-[#E8D8C8] overflow-hidden flex flex-col"
            >
              <div className="relative overflow-hidden">
                <img src={ev.img} alt={ev.title} className="w-full h-56 object-cover img-zoom" />
                <span className="absolute top-4 left-4 bg-[#FAF7F2]/95 backdrop-blur text-[10px] tracking-[0.22em] uppercase text-[#C76D4B] px-3 py-1 rounded-full">
                  {ev.type}
                </span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif-display text-2xl leading-tight">{ev.title}</h3>
                <p className="text-sm text-[#7A5C45] mt-3">{ev.desc}</p>
                <ul className="mt-5 space-y-2 text-xs text-[#4A3320]">
                  <li className="flex gap-2"><Calendar size={14} className="text-[#C76D4B]" /> {ev.date}</li>
                  <li className="flex gap-2"><MapPin size={14} className="text-[#C76D4B]" /> {ev.city}</li>
                  <li className="flex gap-2"><Users size={14} className="text-[#C76D4B]" /> {ev.seats} seats remaining</li>
                </ul>
                <a href="#host-event" className="btn-primary mt-6 text-sm py-3" data-testid={`event-rsvp-${i}`}>
                  RSVP / Enquire <ArrowUpRight size={16} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* HOST AN EVENT */}
      <section id="host-event" className="py-20 sm:py-28 bg-[#F3EBE1]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Host your story here</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Planning something <em className="italic">special?</em>
            </h2>
            <p className="text-[#7A5C45] mt-6 leading-relaxed">
              Birthdays, anniversaries, workshops, brand meetups — share the brief and our events
              team will craft an evening worth talking about.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["Warm spaces", "Flexible setups", "Thoughtful food", "Human moments"].map((t, i) => (
                <li key={t} className="flex gap-3" data-testid={`event-perk-${i}`}>
                  <Check size={16} className="text-[#7B8B6F] mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <form
            onSubmit={onSubmit}
            data-testid="event-enquiry-form"
            className="lg:col-span-7 bg-[#FAF7F2] rounded-[2rem] border border-[#E8D8C8] p-8 sm:p-10"
          >
            {submitted && (
              <div data-testid="event-form-success" className="mb-6 flex items-center gap-3 bg-[#7B8B6F]/12 border border-[#7B8B6F]/30 text-[#4A3320] px-5 py-4 rounded-2xl">
                <Check size={18} className="text-[#7B8B6F]" />
                <span className="text-sm">Got it. Our events team will get in touch soon.</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Name</label>
                <input data-testid="event-form-name" required className="input-line" value={form.name} onChange={update("name")} placeholder="Your name" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Email</label>
                <input data-testid="event-form-email" required type="email" className="input-line" value={form.email} onChange={update("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Phone</label>
                <input data-testid="event-form-phone" required className="input-line" value={form.phone} onChange={update("phone")} placeholder="+91 ..." />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Event type</label>
                <input data-testid="event-form-kind" className="input-line" value={form.kind} onChange={update("kind")} placeholder="Birthday / Workshop / Meetup" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Preferred date</label>
                <input data-testid="event-form-date" type="date" className="input-line" value={form.date} onChange={update("date")} />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Expected guests</label>
                <input data-testid="event-form-guests" className="input-line" value={form.guests} onChange={update("guests")} placeholder="10 / 20 / 50" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Tell us more</label>
                <textarea data-testid="event-form-note" className="input-line min-h-[100px] resize-none" value={form.note} onChange={update("note")} placeholder="Theme, music, dietary requests…" />
              </div>
            </div>
            <button type="submit" className="btn-primary mt-8" data-testid="event-form-submit">
              Plan a Gathering <ArrowUpRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
